import type { AutomationExecution, AutomationExecutionEvaluation } from '@/api/automationApi';
import { formatCurrencyValue, formatNumberValue, parseNumberish } from '@/api/keywordMetrics';

const ACTION_LABELS: Record<string, string> = {
  increase_budget_pct: 'Increase Budget by %',
  increase_budget_amount: 'Increase Budget by amount',
  decrease_budget_pct: 'Decrease Budget by %',
  decrease_budget_amount: 'Decrease Budget by amount',
  set_budget: 'Set Budget',
  pause_campaign: 'Pause Campaign',
  enable_campaign: 'Enable Campaign',
  increase_placement_pct: 'Increase Placement Modifier by %',
  decrease_placement_pct: 'Decrease Placement Modifier by %',
  set_placement_pct: 'Set Placement Modifier',
  placement_value: 'Placement Value',
  send_alert: 'Send Alert',
  send_email: 'Send Email',
  increase_bid_pct: 'Increase Bid by %',
  increase_bid_amount: 'Increase Bid by amount',
  decrease_bid_pct: 'Decrease Bid by %',
  decrease_bid_amount: 'Decrease Bid by amount',
  set_bid: 'Set Bid',
  set_suggested_bid: 'Set Suggested Bid',
  increase_parent_budget_pct: 'Increase Parent Campaign Budget by %',
  increase_parent_budget_amount: 'Increase Parent Campaign Budget by amount',
  decrease_parent_budget_pct: 'Decrease Parent Campaign Budget by %',
  decrease_parent_budget_amount: 'Decrease Parent Campaign Budget by amount',
  set_parent_budget: 'Set Parent Campaign Budget',
  pause_parent_campaign: 'Pause Parent Campaign',
  enable_parent_campaign: 'Enable Parent Campaign',
  pause_portfolio: 'Pause Portfolio',
  enable_portfolio: 'Enable Portfolio',
  pause_ad_group: 'Pause Ad Group',
  enable_ad_group: 'Enable Ad Group',
  pause_target: 'Pause Target',
  enable_target: 'Enable Target',
  pause_product_ad: 'Pause Product Ad',
  enable_product_ad: 'Enable Product Ad',
  add_negative_exact: 'Add Negative Exact',
  add_negative_phrase: 'Add Negative Phrase',
  add_keyword: 'Add Keyword',
};

const PLACEMENT_SLOT_LABELS: Record<string, string> = {
  TOP_OF_SEARCH: 'Top of Search',
  REST_OF_SEARCH: 'Rest of Search',
  PRODUCT_PAGES: 'Product Pages',
  PRODUCT_PAGE: 'Product Pages',
};

const MONEY_FIELDS = new Set(['daily_budget', 'budget', 'bid']);

export type ExecutionChangeSummary = {
  action: string;
  before: string;
  after: string;
};

export function formatActionType(actionType: string | null | undefined): string {
  const raw = (actionType || '').trim();
  if (!raw) return '—';
  return ACTION_LABELS[raw] || titleFromSnake(raw);
}

export function formatExecutionStatus(status: string | null | undefined): string {
  const raw = (status || '').trim();
  if (!raw) return '—';
  if (raw === 'skipped_conditions') return 'conditions not met';
  return raw;
}

export function formatRevertStatus(status: string | null | undefined): string {
  const raw = (status || '').trim();
  if (raw === 'pending') return 'Reverts at day end';
  if (raw === 'running') return 'Reverting';
  if (raw === 'succeeded') return 'Reverted';
  if (raw === 'failed') return 'Revert failed';
  if (raw === 'skipped') return 'Skipped — dayparting';
  return '—';
}

const CONDITION_OPERATORS: Record<string, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  neq: '≠',
  between: 'between',
};

const CONDITION_GROUP_LABELS: Record<string, string> = {
  if: 'IF',
  else_if: 'ELSE IF',
  else: 'ELSE',
};

export type ConditionMetricMeta = {
  key: string;
  label: string;
  unit?: string;
};

export type EvaluatedConditionLine = {
  id: string;
  groupLabel: string;
  showGroupLabel: boolean;
  metricLabel: string;
  actualText: string;
  comparisonText: string;
  relativeText: string | null;
  passed: boolean;
};

function trimNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function formatMetricNumber(value: unknown, unit: string | undefined): string {
  if (value == null || value === '') return 'no data';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (unit === 'string' || unit === 'date') {
    return typeof value === 'string' && value.trim() ? value.trim() : String(value);
  }
  const n = parseNumberish(value);
  if (n == null) return typeof value === 'string' && value.trim() ? value.trim() : 'no data';
  switch (unit) {
    case 'percent':
      return `${trimNumber(n)}%`;
    case 'currency':
      return formatCurrencyValue(n);
    case 'count':
      return formatNumberValue(n, Number.isInteger(n) ? 0 : 2);
    case 'days':
      return `${trimNumber(n)} days`;
    default:
      return trimNumber(n);
  }
}

function metricMeta(
  catalog: ConditionMetricMeta[],
  key: string
): ConditionMetricMeta {
  return catalog.find((item) => item.key === key) ?? { key, label: titleFromSnake(key) };
}

/**
 * One line per condition leaf for a placement execution.
 * Returns null when the row has no structured leaves, so the UI can keep the summary string.
 */
export function evaluatedConditionLines(
  evaluation: AutomationExecutionEvaluation | null | undefined,
  catalog: ConditionMetricMeta[]
): EvaluatedConditionLine[] | null {
  const groups = evaluation?.groups;
  if (!Array.isArray(groups) || groups.length === 0) return null;
  const showGroups = groups.length > 1;
  const lines: EvaluatedConditionLine[] = [];
  groups.forEach((group, groupIndex) => {
    const kind = (group.kind || 'if').toLowerCase();
    const groupLabel = CONDITION_GROUP_LABELS[kind] || kind.toUpperCase();
    const leaves = group.leaves ?? [];
    if (leaves.length === 0) {
      if (kind !== 'else') return;
      lines.push({
        id: `${groupIndex}-else`,
        groupLabel,
        showGroupLabel: showGroups,
        metricLabel: '(always)',
        actualText: '',
        comparisonText: '',
        relativeText: null,
        passed: group.passed === true,
      });
      return;
    }
    leaves.forEach((leaf, leafIndex) => {
      const key = typeof leaf.metric === 'string' ? leaf.metric : '';
      const meta = metricMeta(catalog, key || 'metric');
      const valueType = (leaf.valueType || 'fixed').toLowerCase();
      const relative = valueType === 'relative_pct' || valueType === 'relative_abs';
      const thresholdUnit = valueType === 'relative_pct' ? 'percent' : meta.unit;
      const operator = (leaf.operator || '').toLowerCase();
      const symbol = CONDITION_OPERATORS[operator] || operator;
      const threshold = formatMetricNumber(leaf.value, thresholdUnit);
      const comparisonText =
        operator === 'between'
          ? `between ${threshold} and ${formatMetricNumber(leaf.valueTo, thresholdUnit)}`
          : symbol
            ? `${symbol} ${threshold}`
            : threshold;
      const relativeText = relative
        ? `prev ${formatMetricNumber(leaf.actualPrevious, meta.unit)}, compared ${formatMetricNumber(
            leaf.compared,
            valueType === 'relative_pct' ? 'percent' : meta.unit
          )}`
        : null;
      lines.push({
        id: `${groupIndex}-${leafIndex}-${key}`,
        groupLabel,
        showGroupLabel: showGroups && leafIndex === 0,
        metricLabel: meta.label,
        actualText: formatMetricNumber(leaf.actual, meta.unit),
        comparisonText,
        relativeText,
        passed: leaf.passed === true,
      });
    });
  });
  return lines.length > 0 ? lines : null;
}

/** Condition column text without met/not-met or passed/failed labels. */
export function formatConditionColumnText(summary: string): string {
  return summary.replace(/\s+(?:passed|failed)\b/gi, '');
}

export function formatEvaluationSummary(evaluation: unknown): string {
  if (!evaluation || typeof evaluation !== 'object') return '—';
  const summary = (evaluation as AutomationExecutionEvaluation).summary;
  if (typeof summary === 'string' && summary.trim()) return summary.trim();
  return '—';
}

export function formatEvaluationMetrics(evaluation: unknown): string {
  if (!evaluation || typeof evaluation !== 'object') return '';
  const groups = (evaluation as AutomationExecutionEvaluation).groups;
  if (!Array.isArray(groups)) return '';
  const parts: string[] = [];
  for (const group of groups) {
    for (const leaf of group.leaves ?? []) {
      const metric = typeof leaf.metric === 'string' ? leaf.metric : '';
      if (!metric) continue;
      const actual = leaf.compared !== undefined ? leaf.compared : leaf.actual;
      parts.push(`${metric}=${actual == null || actual === '' ? 'no data' : String(actual)}`);
    }
  }
  return parts.join('; ');
}

export function summarizeStatusCounts(counts: Record<string, number>): string {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) return '—';
  if (entries.length === 1) return formatExecutionStatus(entries[0][0]);
  return entries.map(([status, n]) => `${n} ${formatExecutionStatus(status)}`).join(', ');
}

export function groupBadgeStatus(statuses: string[]): string {
  if (statuses.some((s) => s === 'failed')) return 'failed';
  if (statuses.length > 0 && statuses.every((s) => s === statuses[0])) return statuses[0];
  return 'pending';
}

export function groupBadgeStatusFromCounts(counts: Record<string, number>): string {
  return groupBadgeStatus(
    Object.entries(counts).flatMap(([status, n]) => (n > 0 ? [status] : []))
  );
}

export function formatExecutionValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  if (typeof value !== 'object') return '—';

  const item = value as Record<string, unknown>;
  if (item.placements && typeof item.placements === 'object' && !Array.isArray(item.placements)) {
    return formatPlacements(item.placements as Record<string, unknown>);
  }

  const field = typeof item.field === 'string' ? item.field : null;
  if (field === 'placement_pct') {
    const pct = formatPercent(item.value);
    const slot = formatPlacementSlot(item.placement);
    return slot ? `${slot} ${pct}` : pct;
  }

  if (field && MONEY_FIELDS.has(field)) {
    return formatMoney(item.value);
  }

  if (field === 'state') {
    return scalarText(item.value);
  }

  if (field === 'negative' || field === 'keyword') {
    return formatKeywordLike(item);
  }

  if (typeof item.message === 'string' && item.message.trim()) {
    return item.message.trim();
  }

  if ('value' in item) {
    if (field && (field.includes('budget') || field.includes('bid'))) {
      return formatMoney(item.value);
    }
    return scalarText(item.value);
  }

  return '—';
}

export function executionValueLines(value: unknown): string[] {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const item = value as Record<string, unknown>;
    if (item.placements && typeof item.placements === 'object' && !Array.isArray(item.placements)) {
      const lines = placementLines(item.placements as Record<string, unknown>);
      return lines.length > 0 ? lines : ['—'];
    }
  }
  return [formatExecutionValue(value)];
}

export function pendingRowToChange(row: {
  plannedAction?: Record<string, unknown> | null;
  beforeValue?: unknown;
  afterValue?: unknown;
}): Pick<AutomationExecution, 'actionType' | 'beforeValue' | 'afterValue'> {
  const planned = row.plannedAction ?? null;
  const nestedType =
    planned && typeof planned.action_type === 'string' ? planned.action_type.trim() : '';
  const before = row.beforeValue !== undefined ? row.beforeValue : planned?.before_value;
  const after = row.afterValue !== undefined ? row.afterValue : planned?.after_value;
  return {
    actionType: nestedType,
    beforeValue: before ?? null,
    afterValue: after ?? null,
  };
}

export function pendingEvaluation(row: {
  plannedAction?: Record<string, unknown> | null;
}): AutomationExecutionEvaluation | null {
  const evaluation = row.plannedAction?.evaluation;
  if (!evaluation || typeof evaluation !== 'object' || Array.isArray(evaluation)) return null;
  return evaluation as AutomationExecutionEvaluation;
}

export function summarizeGroupChange(
  items: Pick<AutomationExecution, 'actionType' | 'beforeValue' | 'afterValue'>[]
): ExecutionChangeSummary {
  if (items.length === 0) {
    return { action: '—', before: '—', after: '—' };
  }

  const actionTypes = new Set(items.map((row) => row.actionType));
  const action =
    actionTypes.size === 1
      ? formatActionType(items[0].actionType)
      : `${actionTypes.size} actions`;

  const befores = items.map((row) => formatExecutionValue(row.beforeValue));
  const afters = items.map((row) => formatExecutionValue(row.afterValue));
  return {
    action,
    before: withMoreSuffix(befores),
    after: withMoreSuffix(afters),
  };
}

function withMoreSuffix(values: string[]): string {
  const first = values[0] ?? '—';
  const extra = values.slice(1).filter((value) => value !== first).length;
  if (extra === 0) return first;
  return `${first} +${extra} more`;
}

function formatKeywordLike(item: Record<string, unknown>): string {
  const parts: string[] = [];
  const term = scalarText(item.value);
  if (term !== '—') parts.push(term);
  const match = scalarText(item.match);
  if (match !== '—') parts.push(match);
  const bid = parseNumberish(item.bid);
  if (bid != null) parts.push(formatMoney(bid));
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function placementLines(placements: Record<string, unknown>): string[] {
  return Object.entries(placements)
    .map(([slot, raw]) => {
      const label = formatPlacementSlot(slot);
      if (!label) return null;
      return `${label} ${formatPercent(raw)}`;
    })
    .filter((part): part is string => Boolean(part));
}

function formatPlacements(placements: Record<string, unknown>): string {
  const parts = placementLines(placements);
  return parts.length > 0 ? parts.join(', ') : '—';
}

function formatPlacementSlot(slot: unknown): string {
  if (typeof slot !== 'string' || !slot.trim()) return '';
  const key = slot.trim();
  return PLACEMENT_SLOT_LABELS[key] || titleFromSnake(key.toLowerCase());
}

function formatMoney(value: unknown): string {
  const n = parseNumberish(value);
  if (n == null) return '—';
  return formatCurrencyValue(n);
}

function formatPercent(value: unknown): string {
  const n = parseNumberish(value);
  if (n == null) return scalarText(value);
  return `${n}%`;
}

function scalarText(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  return '—';
}

function titleFromSnake(raw: string): string {
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
